/**
 * Web Vitals reporting.
 *
 * Uses the native PerformanceObserver API to measure LCP, CLS, INP,
 * FCP, and TTFB. Each metric fires a `web_vital` `reportEvent`
 * (routed to Sentry as a breadcrumb via `installSentryReporter`).
 * Bundles cleanly because we don't pull a `web-vitals` package — the
 * computations are a few lines each and the API surface is stable
 * across all our supported browsers.
 *
 * Why not the `web-vitals` npm package: adds ~3kB and a dependency
 * we can't audit easily. The official Google `web-vitals` library
 * does the same observer dance with a few more polish features
 * (attribution, debug); we don't need either at our current scale.
 *
 * Why not Sentry's built-in tracing: Sentry surfaces vitals on the
 * Performance tab but doesn't fire them through the reporter seam,
 * so consumers of `reportEvent` (e.g., a future product-analytics
 * adapter) miss them. The explicit emit gives us a clean handle.
 *
 * Threshold buckets ("good" / "needs-improvement" / "poor") follow
 * the Core Web Vitals 2024 cutoffs. Each metric carries its bucket
 * so dashboards can pivot without re-evaluating.
 */

import { reportEvent } from './monitoring';

type Bucket = 'good' | 'needs_improvement' | 'poor';

interface IThresholds {
  good: number;
  poor: number;
}

// Per https://web.dev/articles/vitals (2024 thresholds).
const THRESHOLDS: Record<string, IThresholds> = {
  LCP: { good: 2500, poor: 4000 }, // ms
  CLS: { good: 0.1, poor: 0.25 }, // unitless
  INP: { good: 200, poor: 500 }, // ms
  FCP: { good: 1800, poor: 3000 }, // ms
  TTFB: { good: 800, poor: 1800 }, // ms
};

function bucketFor(metric: string, value: number): Bucket {
  const t = THRESHOLDS[metric];
  if (!t) return 'good';
  if (value <= t.good) return 'good';
  if (value <= t.poor) return 'needs_improvement';
  return 'poor';
}

function emit(metric: string, value: number, attrs: Record<string, unknown> = {}): void {
  reportEvent('web_vital', {
    metric,
    value: Math.round(value * 1000) / 1000,
    bucket: bucketFor(metric, value),
    ...attrs,
  });
}

/**
 * Largest Contentful Paint — time at which the largest content
 * element in the viewport became visible.
 */
function observeLCP(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  let lcp = 0;
  try {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        lcp = entry.startTime;
      }
    });
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
    // Fire once on hide — same convention as web.dev's reference impl.
    const flush = (): void => {
      if (lcp > 0) emit('LCP', lcp);
      observer.disconnect();
      document.removeEventListener('visibilitychange', onHidden);
    };
    const onHidden = (): void => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onHidden);
    // Also fire on pagehide for browsers that skip visibilitychange.
    window.addEventListener('pagehide', flush, { once: true });
  } catch {
    // Unsupported entry type — silently no-op (older browsers).
  }
}

/**
 * Cumulative Layout Shift — sum of all unexpected layout shifts that
 * occurred during the page's lifetime, grouped into sessions of
 * <500ms gaps capped at 5s windows (per the CLS spec).
 */
function observeCLS(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  let clsValue = 0;
  let sessionValue = 0;
  let sessionEntries: PerformanceEntry[] = [];
  try {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries() as Array<
        PerformanceEntry & { value: number; hadRecentInput: boolean }
      >) {
        if (entry.hadRecentInput) continue;
        const firstEntry = sessionEntries[0];
        const lastEntry = sessionEntries[sessionEntries.length - 1];
        if (
          sessionEntries.length > 0 &&
          (entry.startTime - lastEntry.startTime > 1000 ||
            entry.startTime - firstEntry.startTime > 5000)
        ) {
          sessionValue = 0;
          sessionEntries = [];
        }
        sessionValue += entry.value;
        sessionEntries.push(entry);
        if (sessionValue > clsValue) clsValue = sessionValue;
      }
    });
    observer.observe({ type: 'layout-shift', buffered: true });
    const flush = (): void => {
      emit('CLS', clsValue);
      observer.disconnect();
    };
    window.addEventListener('pagehide', flush, { once: true });
  } catch {
    // Unsupported.
  }
}

/**
 * Interaction to Next Paint — duration between a user interaction
 * and the next paint. The implementation uses `event` timing
 * entries with `duration >= 40ms` (the spec's noise floor).
 */
function observeINP(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  let worst = 0;
  try {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries() as Array<
        PerformanceEntry & { duration: number; interactionId?: number }
      >) {
        // Only interaction-typed events have a non-zero interactionId.
        if (entry.interactionId === undefined || entry.interactionId === 0) {
          continue;
        }
        if (entry.duration > worst) {
          worst = entry.duration;
        }
      }
    });
    observer.observe({ type: 'event', buffered: true, durationThreshold: 40 } as PerformanceObserverInit);
    const flush = (): void => {
      if (worst > 0) emit('INP', worst);
      observer.disconnect();
    };
    window.addEventListener('pagehide', flush, { once: true });
  } catch {
    // Unsupported.
  }
}

/**
 * First Contentful Paint — same observer as LCP but a different
 * entry name.
 */
function observeFCP(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  try {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          emit('FCP', entry.startTime);
          observer.disconnect();
        }
      }
    });
    observer.observe({ type: 'paint', buffered: true });
  } catch {
    // Unsupported.
  }
}

/**
 * Time to First Byte — direct from the navigation timing entry.
 */
function reportTTFB(): void {
  if (typeof performance === 'undefined') return;
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (nav) {
    emit('TTFB', nav.responseStart - nav.startTime);
  }
}

/**
 * Initialize every observer. Idempotent at the call-site level —
 * call once from `main.tsx` (or wherever the app boot is).
 */
export function initWebVitals(): void {
  observeLCP();
  observeCLS();
  observeINP();
  observeFCP();
  reportTTFB();
}
